import React from "react";
import {
  Box,
  Typography,
  Paper,
  alpha,
  useTheme,
  AppBar,
  Toolbar,
  Icon,
  Grid,
  IconButton,
  Button,
  Stack,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import { SessionCheck } from "../../components/SessionCheck/SessionCheck";
import { useProducts } from "../../hooks/useProducts";
import { useCart } from "../../hooks/useCart";
import { PaymentMethodSelector } from "../../components/PaymentMethodSelector/PaymentMethodSelector";
import { CashPaymentDialog } from "../../components/PaymentDialogs/CashPaymentDialog";
import { CardPaymentDialog } from "../../components/PaymentDialogs/CardPaymentDialog";
import { BillSummaryDialog } from "../../components/PaymentDialogs/BillSummaryDialog";
import { usePOSService } from "../../hooks/usePOSService";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

interface Category {
  id: string;
  name: string;
  icon: string;
  color: string;
  itemCount: number;
}

interface MenuItem {
  id: string;
  name: string;
  price: number;
  category: string;
  description?: string;
  quantity?: number;
}

export const MenuPage: React.FC = () => {
  const theme = useTheme();
  const { products, loading } = useProducts();
  const {
    items,
    addItem,
    removeItem,
    updateQuantity,
    total,
    subtotal,
    tax,
    clearCart,
  } = useCart();
  const { posService: service } = usePOSService();
  const navigate = useNavigate();

  const [selectedCategory, setSelectedCategory] = React.useState<string | null>(
    null
  );
  const [selectedPayment, setSelectedPayment] = React.useState<string>("cash");
  const [cashPaymentOpen, setCashPaymentOpen] = React.useState(false);
  const [cardPaymentOpen, setCardPaymentOpen] = React.useState(false);
  const [billSummaryOpen, setBillSummaryOpen] = React.useState(false);
  const [paymentDetails, setPaymentDetails] = React.useState<{
    amountPaid?: number;
    change?: number;
  }>({});
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [orderResponse, setOrderResponse] = React.useState<any>(null);

  // Helper functions for category icons and colors
  const getCategoryIcon = (category: string): string => {
    const icons: Record<string, string> = {
      Breakfast: "free_breakfast",
      Soups: "soup_kitchen",
      Pasta: "restaurant",
      Sushi: "set_meal",
      "Main Course": "dinner_dining",
      dessert: "cake",
      Drinks: "local_bar",
      Alcohol: "wine_bar",
      Pizza: "local_pizza",
      Burgers: "lunch_dining",
      Sandwiches: "fastfood",
      Salads: "emoji_nature",
      Seafood: "set_meal",
      Appetizers: "restaurant_menu",
      BBQ: "outdoor_grill",
      Vegan: "spa",
      Vegetarian: "eco",
      Asian: "ramen_dining",
      Indian: "restaurant",
      Mexican: "local_dining",
      Italian: "local_pizza",
      French: "bakery_dining",
      Mediterranean: "emoji_food_beverage",
      Snacks: "fastfood",
      IceCream: "icecream",
      Coffee: "coffee",
      Tea: "emoji_food_beverage",
      Juices: "local_cafe",
      Cocktails: "liquor",
      Beers: "sports_bar",
      Wines: "nightlife",
      HotDrinks: "local_cafe",
      ColdDrinks: "local_bar",
      Smoothies: "blender",
      Grilled: "outdoor_grill",
      Rice: "ramen_dining",
      Noodles: "lunch_dining",
      Steaks: "dinner_dining",
      FingerFood: "tap_and_play",
    };

    return icons[category] || "restaurant_menu";
  };

  const getCategoryColor = (category: string): string => {
    const colors: Record<string, string> = {
      Breakfast: "#90caf9", // Light blue
      Soups: "#f48fb1", // Pink
      Pasta: "#81d4fa", // Light blue
      Sushi: "#b39ddb", // Purple
      "Main course": "#ffab91", // Light pink
      dessert: "#ce93d8", // Light purple
      Drinks: "#f48fb1", // Pink
      Alcohol: "#80cbc4", // Teal
    };
    return colors[category] || "#757575";
  };

  // Group products by category and add quantity
  const categories: Category[] = React.useMemo(() => {
    const categoryMap = products.reduce((acc, product) => {
      const cat = acc.find((c) => c.id === product.category);
      if (!cat) {
        acc.push({
          id: product.category,
          name: product.category,
          icon: getCategoryIcon(product.category),
          color: getCategoryColor(product.category),
          itemCount: 1,
        });
      } else {
        cat.itemCount++;
      }
      return acc;
    }, [] as Category[]);
    return categoryMap;
  }, [products]);

  // Filter products by selected category and add quantities
  const menuItems = React.useMemo(() => {
    const productsWithQuantity = products.map((product) => ({
      ...product,
      quantity:
        items.find((item) => item.product_id === product._id)?.quantity || 0,
    }));
    if (!selectedCategory) return productsWithQuantity;
    return productsWithQuantity.filter(
      (product) => product.category === selectedCategory
    );
  }, [products, selectedCategory, items]);

  const handleQuantityChange = (productId: string, newQuantity: number) => {
    const product = products.find((p) => p._id === productId);
    if (!product) return;

    if (newQuantity === 0) {
      removeItem(productId);
    } else if (items.find((item) => item.product_id === productId)) {
      updateQuantity(productId, newQuantity);
    } else {
      addItem(product);
      updateQuantity(productId, newQuantity);
    }
  };

  const handlePlaceOrder = () => {
    if (selectedPayment === "cash") {
      setCashPaymentOpen(true);
    } else {
      setCardPaymentOpen(true);
    }
  };

  const createOrder = async (paymentDetails: {
    method: "CASH" | "CARD" | "MOBILE";
    amount: number;
    reference?: string;
  }) => {
    setIsProcessing(true);
    setError(null);
    try {
      const orderData = {
        items: items.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          price: item.price
        })),
        payment: {
          method: paymentDetails.method,
          amount: paymentDetails.amount,
          reference: paymentDetails.reference
        }
      };

      const response = await service.createOrder(orderData);
      if (response) {
        setOrderResponse(response);
        setBillSummaryOpen(true);
        clearCart();
        return response;
      }
      throw new Error('No response from server');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create order';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCashPayment = async (amountPaid: number) => {
    setCashPaymentOpen(false);

    const order = await createOrder({
      method: "CASH",
      amount: amountPaid,
    });

    if (order) {
      setPaymentDetails({
        amountPaid,
        change: amountPaid - total,
      });
      setBillSummaryOpen(true);
    }
  };

  const handleCardPayment = async (success: boolean) => {
    setCardPaymentOpen(false);

    if (success) {
      const order = await createOrder({
        method: "CARD",
        amount: total,
        reference: new Date().getTime().toString(),
      });

      if (order) {
        setPaymentDetails({
          amountPaid: total,
        });
        setBillSummaryOpen(true);
      }
    }
  };

  const handleBillSummaryClose = () => {
    setBillSummaryOpen(false);
    clearCart();
    navigate("/pos/orders");
  };

  const handlePrintReceipt = async () => {
    try {
      // TODO: Implement receipt printing
      toast.success("Order placed successfully!");
      handleBillSummaryClose();
    } catch (error) {
      console.error("Error printing receipt:", error);
      toast.error("Error printing receipt");
    }
  };

  return (
    <SessionCheck>
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.default",
        }}
      >
        <AppBar
          position="static"
          elevation={0}
          sx={{ backgroundColor: "#1E8078" }}
        >
          <Toolbar>
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
              Menu
            </Typography>
          </Toolbar>
        </AppBar>

        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Main Content Area */}
          {loading ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {/* Categories Skeleton */}
              <Stack spacing={2}>
                <Box sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    {[1, 2, 3, 4, 5, 6].map((item) => (
                      <Grid item xs={12} sm={6} md={3} key={item}>
                        <Paper
                          elevation={1}
                          sx={{
                            p: 3,
                            height: "100%",
                            minHeight: 120,
                            backgroundColor: alpha(
                              theme.palette.background.paper,
                              0.95
                            ),
                            backdropFilter: "blur(8px)",
                            borderRadius: 1,
                            border: `1px solid ${alpha(
                              theme.palette.divider,
                              0.2
                            )}`,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Skeleton
                              variant="circular"
                              width={24}
                              height={24}
                            />
                            <Skeleton width={120} height={32} />
                          </Box>
                          <Skeleton width={80} height={24} sx={{ mt: 1 }} />
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Menu Items Skeleton */}
                <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
                  <Grid container spacing={2}>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={item}>
                        <Paper
                          elevation={1}
                          sx={{
                            p: 2,
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            backgroundColor: alpha(
                              theme.palette.background.paper,
                              0.95
                            ),
                            backdropFilter: "blur(8px)",
                            borderRadius: 1,
                            border: `1px solid ${alpha(
                              theme.palette.divider,
                              0.2
                            )}`,
                          }}
                        >
                          <Skeleton
                            variant="text"
                            width="80%"
                            height={32}
                            sx={{ mb: 1 }}
                          />
                          <Skeleton
                            variant="text"
                            width="40%"
                            height={24}
                            sx={{ mb: 2 }}
                          />
                          <Skeleton
                            variant="text"
                            width="30%"
                            height={32}
                            sx={{ mb: 2 }}
                          />
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mt: "auto",
                            }}
                          >
                            <Skeleton
                              variant="circular"
                              width={32}
                              height={32}
                            />
                            <Skeleton width={40} height={32} />
                            <Skeleton
                              variant="circular"
                              width={32}
                              height={32}
                            />
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Stack>
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Stack spacing={2}>
                {/* Categories */}
                <Box sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Paper
                        elevation={1}
                        onClick={() => setSelectedCategory(null)}
                        sx={{
                          p: 3,
                          height: "100%",
                          minHeight: 120,
                          cursor: "pointer",
                          backgroundColor: !selectedCategory
                            ? alpha(theme.palette.primary.main, 0.1)
                            : alpha(theme.palette.background.paper, 0.95),
                          backdropFilter: "blur(8px)",
                          borderRadius: 1,
                          transition: "all 0.2s",
                          border: `1px solid ${
                            !selectedCategory
                              ? theme.palette.primary.main
                              : alpha(theme.palette.divider, 0.2)
                          }`,
                          "&:hover": {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.1
                            ),
                            transform: "translateY(-2px)",
                            boxShadow: theme.shadows[2],
                          },
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Icon>restaurant_menu</Icon>
                          <Typography variant="h6">All Items</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {products.length} items
                        </Typography>
                      </Paper>
                    </Grid>
                    {categories.map((category) => (
                      <Grid item xs={12} sm={6} md={3} key={category.id}>
                        <Paper
                          elevation={1}
                          onClick={() => setSelectedCategory(category.id)}
                          sx={{
                            p: 3,
                            height: "100%",
                            minHeight: 120,
                            cursor: "pointer",
                            backgroundColor:
                              selectedCategory === category.id
                                ? alpha(category.color, 0.1)
                                : alpha(theme.palette.background.paper, 0.95),
                            backdropFilter: "blur(8px)",
                            borderRadius: 1,
                            transition: "all 0.2s",
                            border: `1px solid ${
                              selectedCategory === category.id
                                ? category.color
                                : alpha(theme.palette.divider, 0.2)
                            }`,
                            "&:hover": {
                              backgroundColor: alpha(category.color, 0.1),
                              transform: "translateY(-2px)",
                              boxShadow: theme.shadows[2],
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Icon sx={{ color: category.color }}>
                              {category.icon}
                            </Icon>
                            <Typography variant="h6">
                              {category.name}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {category.itemCount} items
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
                <Divider sx={{ mb: 2 }} />
                {/* Menu Items */}
                <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
                  <Grid container spacing={2}>
                    {menuItems.map((item) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                        <Paper
                          elevation={1}
                          sx={{
                            p: 2,
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            backgroundColor: alpha(
                              theme.palette.background.paper,
                              0.95
                            ),
                            backdropFilter: "blur(8px)",
                            borderRadius: 1,
                            border: `1px solid ${alpha(
                              theme.palette.divider,
                              0.2
                            )}`,
                            "&:hover": {
                              boxShadow: theme.shadows[2],
                            },
                          }}
                        >
                          <Typography variant="h6" gutterBottom>
                            {item.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            {item.category}
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{ mb: 2, color: "#1E8078" }}
                          >
                            BHD {item.price.toFixed(3)}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mt: "auto",
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleQuantityChange(
                                  item._id,
                                  (item.quantity || 0) - 1
                                )
                              }
                              disabled={!item.quantity}
                            >
                              <Icon>remove</Icon>
                            </IconButton>
                            <Typography
                              sx={{ minWidth: 40, textAlign: "center" }}
                            >
                              {item.quantity || 0}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleQuantityChange(
                                  item._id,
                                  (item.quantity || 0) + 1
                                )
                              }
                            >
                              <Icon>add</Icon>
                            </IconButton>
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Stack>
            </Box>
          )}

          {/* Cart */}
          <Paper
            elevation={0}
            sx={{
              width: 400,
              display: "flex",
              flexDirection: "column",
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              backdropFilter: "blur(8px)",
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                backgroundColor: alpha(theme.palette.primary.main, 0.03),
                backdropFilter: "blur(8px)",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Icon
                  sx={{
                    color: "#1E8078",
                    fontSize: 24,
                  }}
                >
                  shopping_cart
                </Icon>
                <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                  Cart
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "#1E8078",
                    color: "white",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 6,
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <span>{items.length}</span>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {items.length === 1 ? "item" : "items"}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Cart Items */}
            <Box sx={{ flex: 1, overflow: "auto", mt: 1 }}>
              {items.map((item, index) => (
                <Box
                  key={item.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    mx: 2,
                    my: 1,
                    backgroundColor: theme.palette.background.default,

                    backdropFilter: "blur(8px)",
                    borderRadius: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: "#1E8078",
                        borderRadius: 5,
                        p: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          color: "white",
                          fontSize: "0.875rem",
                          mx: 1,
                          my: 0.1,
                        }}
                      >
                        {index + 1}
                      </Typography>
                    </Box>

                    <Typography>
                      {item.name}{" "}
                      <span
                        style={{
                          fontSize: "10px",
                          fontWeight: 700,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        x{item.quantity}
                      </span>
                    </Typography>
                  </Box>
                  <Typography>
                    BHD {(item.price * item.quantity).toFixed(3)}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Cart Summary */}
            <Box
              sx={{ p: 2, bgcolor: alpha(theme.palette.background.paper, 0.9) }}
            >
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography color="text.secondary">Subtotal</Typography>
                <Typography>BHD {subtotal.toFixed(3)}</Typography>
              </Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <Typography color="text.secondary">Tax 10%</Typography>
                <Typography>BHD {tax.toFixed(3)}</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}
              >
                <Typography variant="h6">Total</Typography>
                <Typography variant="h6">BHD {total.toFixed(3)}</Typography>
              </Box>

              {/* Payment Methods */}
              <Typography color="text.secondary" sx={{ mb: 1 }}>
                Payment Method
              </Typography>
              <PaymentMethodSelector
                selectedPayment={selectedPayment}
                onSelectPayment={setSelectedPayment}
              />

              <Button
                variant="contained"
                fullWidth
                size="large"
                disabled={items.length === 0}
                onClick={handlePlaceOrder}
                sx={{
                  backgroundColor: "#1E8078",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#1E8078",
                  },
                }}
              >
                Place Order
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>

      {/* Loading Overlay */}
      {isProcessing && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Paper
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              backgroundColor: alpha(theme.palette.background.paper, 0.9),
              backdropFilter: "blur(10px)",
              borderRadius: 2,
            }}
          >
            <CircularProgress />
            <Typography>Processing Order...</Typography>
          </Paper>
        </Box>
      )}

      {/* Payment Dialogs */}
      <CashPaymentDialog
        open={cashPaymentOpen}
        onClose={() => setCashPaymentOpen(false)}
        onConfirm={handleCashPayment}
        totalAmount={total}
      />

      <CardPaymentDialog
        open={cardPaymentOpen}
        onClose={() => setCardPaymentOpen(false)}
        onConfirm={handleCardPayment}
        totalAmount={total}
        paymentMethod={selectedPayment === "mastercard" ? "card" : "benefitpay"}
      />

      <BillSummaryDialog
        open={billSummaryOpen}
        onClose={handleBillSummaryClose}
        onPrint={handlePrintReceipt}
        items={items}
        subtotal={subtotal}
        tax={tax}
        total={total}
        paymentMethod={selectedPayment}
        amountPaid={paymentDetails.amountPaid}
        change={paymentDetails.change}
      />
    </SessionCheck>
  );
};
